import React from 'react';
// Import shared types (adjust path as needed)
// import { Metric } from '../../types/types';

// --- Type Definition (Copied from parent for standalone clarity) ---
export interface Metric {
  category: string;
  item: string;
  value: string | number;
  year: number;
  source?: 'confirmed' | 'manual' | 'ocr';
  readOnly?: boolean;
  tempId?: string; // Optional temporary ID for list keys
}

interface RevenueExpenseSectionProps {
  items: Metric[];
  years: number[];
  onChange: (updatedItems: Metric[]) => void; // Callback to update parent state
  disabled?: boolean;
}

const RevenueExpenseSection: React.FC<RevenueExpenseSectionProps> = ({ items, years, onChange, disabled }) => {
  // Handler for when an input value changes
  const handleValueChange = (tempId: string | undefined, year: number, newValue: string) => {
    if (!tempId) return; // Should not happen if tempId is always assigned

    const updatedItems = items.map((item) =>
      item.tempId === tempId && item.year === year // Find item by unique ID and year
        ? { ...item, value: newValue } // Update value (keep as string for input)
        : item,
    );
    // If the specific year entry didn't exist, we need to add it.
    // This requires a more complex check based on category/item grouping.
    // For simplicity here, we assume the item structure passed in is complete for the years.
    // A more robust solution would handle adding new year values to existing category/item rows.

    onChange(updatedItems); // Notify parent component of the change
  };

  // Group items by category and item name for table rendering
  // This assumes multiple years exist for the same category/item combination
  const groupedItems = items.reduce((acc, item) => {
    const key = `${item.category}-${item.item}`; // Unique key per line item type
    if (!acc[key]) {
      acc[key] = {
        category: item.category,
        item: item.item,
        readOnly: item.readOnly, // Assume readOnly is consistent for the item
        tempId: item.tempId, // Use the ID from the first occurrence
        valuesByYear: {},
      };
    }
    // Store value against the year
    acc[key].valuesByYear[item.year] = item.value;
    return acc;
  }, {} as Record<string, { category: string; item: string; readOnly?: boolean; tempId?: string; valuesByYear: Record<number, string | number> }>);

  return (
    <div className="p-4 border rounded mb-4 shadow-sm">
      {' '}
      {/* Example styling */}
      <h3 className="text-lg font-semibold mb-3">Revenue & Expenses</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {' '}
          {/* Example table styling */}
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
              {years.map((year) => (
                <th key={year} className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.values(groupedItems).map((group) => (
              <tr key={group.tempId}>
                {' '}
                {/* Use tempId for the key */}
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{group.category}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{group.item}</td>
                {years.map((year) => (
                  <td key={year} className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">
                    <input
                      type="number"
                      value={group.valuesByYear[year] ?? ''} // Use value for the specific year, default to empty string
                      onChange={(e) => handleValueChange(group.tempId, year, e.target.value)}
                      disabled={disabled || group.readOnly}
                      className={`w-full p-1 border rounded text-right ${group.readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`} // Example styling
                      step="any" // Allow decimals
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Placeholder for adding/removing rows - requires more complex state management */}
      {/* <div className="mt-3">
        <button className="text-sm text-blue-600 hover:underline">+ Add Revenue/Expense Line</button>
      </div> */}
    </div>
  );
};

export default RevenueExpenseSection;
